<template>
  <draggable
    class="
      w-full
      border
      rounded-lg
      text-secondary-invert
      border-secondary-light
      bg-secondary
      max-h-100
      overflow-y-auto
      custom-scrollbars
    "
    ref="sortableElement"
    v-bind="dragOptions"
    v-model="sortableList"
    draggable=".gp-draggable"
    handle=".drag-handle"
    @start="dragStart"
    @end="dragEnd"
    @change="cleanup"
  >
    <transition-group type="transition" name="flip-list" tag="div">
      <div v-for="(item,idx) in list" :key="item.id" class="gp-draggable">
        <div
          v-bind="item.data"
          :dragging="dragging"
          class="flex items-center p-2 border-secondary-light"
          :class="{'border-b': idx < list.length - 1}"
        >
          <draggable-icon
            class="drag-handle cursor-move w-3 mr-2"
            v-show="!disableSort"
          ></draggable-icon>
          <input
            type="text"
            v-model="item.text"
            class="w-full bg-transparent border-none focus:outline-none"
          />
          <div
            class="relative mr-2"
            v-if="linkList"
            v-on-click-outside="closeLinkList"
          >
            <button
              class="text-xs hover:text-primary"
              :class="{'text-primary': item.link, 'text-secondary-invert': !item.link}"
              title="Link Tab Head"
              @click.stop="openLinkList(item.id)"
            >
              <iconLink />
            </button>
            <ul
              class="gp-sortable-link-list absolute top-full right-0 w-32 z-20 rounded-lg bg-secondary-darker border border-secondary-light overflow-hidden shadow-lg"
              v-show="isLinkListOpen && currentLinklist === item.id"
            >
              <option
                class="gp-sortable-link-list--item cursor-pointer p-2 text-sm border-b border-secondary"
                :class="{ 'bg-secondary-light': item.link === null }"
                @click="setLink(item.id, null)"
              >
                None
              </option>
              <option
                v-for="link in Object.keys(linkList)"
                :key="link"
                :disabled="isLinkUsed(linkList[link], item.link)"
                class="gp-sortable-link-list--item cursor-pointer p-2 text-sm border-b border-secondary"
                :class="{ 'bg-secondary-light': item.link === linkList[link], 'cursor-not-allowed': isLinkUsed(linkList[link], item.link) }"
                @click="setLink(item.id, linkList[link])"
                :title="isLinkUsed(linkList[link], item.link) ? 'Linked with another tab-content' : null"
              >
                {{ link }}
              </option>
            </ul>
          </div>
          <button
            v-if="activeMarker"
            class="text-sm hover:text-primary mr-2"
            :class="{ 'text-primary': item.active, 'text-secondary-invert': !item.active }"
            title="Make default active"
            @click="markActiveItem(item.id)"
          >
            <svg
              v-show="item.active"
              class="svg-inline--fa fa-dot-circle fa-w-16"
              style="display: none;"
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="dot-circle"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              data-fa-i2svg=""
            >
              <path
                fill="currentColor"
                d="M256 56c110.532 0 200 89.451 200 200 0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-110.532 89.451-200 200-200m0-48C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 168c-44.183 0-80 35.817-80 80s35.817 80 80 80 80-35.817 80-80-35.817-80-80-80z"
              ></path>
            </svg>
            <svg
              v-show="!item.active"
              class="svg-inline--fa fa-circle fa-w-16"
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="circle"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              data-fa-i2svg=""
            >
              <path
                fill="currentColor"
                d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200z"
              ></path>
            </svg>
          </button>
          <button
            class="text-xs text-secondary-invert hover:text-primary"
            title="Delete"
            v-if="list.length > 1"
            @click="deleteListItem(item.id)"
          >
            <iconTrash />
          </button>
        </div>
      </div>
    </transition-group>
  </draggable>
</template>
<script>
import draggable from 'vuedraggable';
import DraggableIcon from '../assets/draggable-icon.svg';
import iconLink from '../assets/icon-link.svg';
import iconTrash from '../assets/icon-trash.svg';
import directives from '../directives';

export default {
  name: 'SortableListPlain',
  components: {
    iconTrash,
    iconLink,
    draggable,
    DraggableIcon
  },
  directives,
  props: {
    value: {
      required: false,
      type: Array,
      default: null
    },
    list: {
      required: true,
      type: Array
    },
    linkList: {
      type: Object,
      required: false,
      default: null
    },
    activeMarker: {
      type: Boolean,
      required: false,
      default: false
    },
    disableSort: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      dragging: false,
      isLinkListOpen: false,
      currentLinklist: null
    };
  },
  computed: {
    dragOptions() {
      return {
        swapThreshold: 0.75,
        animation: 75,
        group: 'listitem',
        disabled: this.disableSort,
        ghostClass: 'ghost'
      };
    },
    sortableList: {
      get() {
        this.$emit('input', this.list);
        return this.list;
      },
      set(value) {
        this.$emit('change', value);
      }
    },
  },
  methods: {
    dragStart() {
      this.dragging = true;
    },
    dragEnd() {
      this.dragging = false;
      this.cleanup();
    },
    cleanup() {
      document.querySelectorAll('.gp-draggable').forEach(element => {
        element.removeAttribute('style');
      });
    },
    deleteListItem(id) {
      const idxToDelete = this.list.findIndex(item => item.id === id);
      this.list.splice(idxToDelete, 1);
      this.$emit('change', this.list);
    },
    markActiveItem(id) {
      this.list.forEach(item => {
        item.active = item.id === id;
      });
      this.$emit('change', this.list);
    },
    openLinkList(id) {
      this.isLinkListOpen = true;
      this.currentLinklist = id;
    },
    closeLinkList() {
      this.isLinkListOpen = false;
      this.currentLinklist = null;
    },
    setLink(id, link) {
      this.list.forEach(item => {
        if (id === item.id) {
          item.link = link;
        }
      });
      this.$emit('change', this.list);
    },
    isLinkUsed(link, itemLink) {
      if (itemLink === link) return false;
      const usedLinks = this.list.map(item => item?.link);
      return usedLinks.includes(link);
    }
  },
  watch: {
    list(newVal, oldVal) {
      if (newVal.length > oldVal.length) {
        this.$nextTick(() => {
          const sortableElementDOM = this.$refs.sortableElement?.$el;
          if (sortableElementDOM) {
            sortableElementDOM.scroll({ top: sortableElementDOM.scrollHeight, behavior: 'smooth' });
          }
        });
      }
    }
  }
};
</script>
<style lang="scss">
.flip-list-move {
  transition: transform 0.5s;
}
.gp-sortable-link-list {
  &--item {
    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
